import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LangProvider } from "./context/LangContext";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Layout from "./components/Layout";
import Lobby from "./pages/Lobby";
import SpinWheel from "./pages/SpinWheel";
import ColorPrediction from "./pages/ColorPrediction";
import DiceGame from "./pages/DiceGame";
import ScratchCard from "./pages/ScratchCard";
import Wallet from "./pages/Wallet";
import Leaderboard from "./pages/Leaderboard";
import Profile from "./pages/Profile";
import Payments from "./pages/Payments";
import Rules from "./pages/Rules";
import Admin from "./pages/Admin";
import AdminLogin from "./pages/AdminLogin";
import AdminShell from "./components/AdminShell";
import TeenPatti from "./pages/TeenPatti";
import AndarBahar from "./pages/AndarBahar";
import Aviator from "./pages/Aviator";
import DragonTiger from "./pages/DragonTiger";
import History from "./pages/History";
import Support from "./pages/Support";

function FullLoader() {
  return (
    <div className="min-h-screen grid place-items-center text-amber-300">
      <Loader2 size={34} className="animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user || user.role !== "admin") return <Navigate to="/admin" replace />;
  return children;
}

function AdminPublic({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (user && user.role === "admin") return <Navigate to="/admin/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/app" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Lobby />} />
        <Route path="spin" element={<SpinWheel />} />
        <Route path="color" element={<ColorPrediction />} />
        <Route path="dice" element={<DiceGame />} />
        <Route path="scratch" element={<ScratchCard />} />
        <Route path="teenpatti" element={<TeenPatti />} />
        <Route path="andarbahar" element={<AndarBahar />} />
        <Route path="aviator" element={<Aviator />} />
        <Route path="dragontiger" element={<DragonTiger />} />
        <Route path="support" element={<Support />} />
        <Route path="wallet" element={<Wallet />} />
        <Route path="leaderboard" element={<Leaderboard />} />
        <Route path="profile" element={<Profile />} />
        <Route path="payments" element={<Payments />} />
        <Route path="rules" element={<Rules />} />
        <Route path="history" element={<History />} />
        <Route path="admin" element={<Navigate to="/admin/dashboard" replace />} />
      </Route>
      <Route path="/admin" element={<AdminPublic><AdminLogin /></AdminPublic>} />
      <Route path="/admin/dashboard" element={<AdminRoute><AdminShell /></AdminRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LangProvider>
        <AuthProvider>
          <Toaster position="top-center" theme="dark" richColors />
          <AppRouter />
        </AuthProvider>
      </LangProvider>
    </BrowserRouter>
  );
}
